import type { OnChanges,
	SimpleChange, SimpleChanges, TemplateRef } from '@angular/core';
import {
	ChangeDetectionStrategy, Component, ElementRef, Input, Renderer2
} from '@angular/core';

import { Enumeration } from '@bp/shared/models/core/enum';
import type { PropertyMetadataTable } from '@bp/shared/models/metadata';
import { FieldViewType, PropertyMetadata } from '@bp/shared/models/metadata';
import { DisplayValuePipe } from '@bp/shared/pipes';
import { MASK_SUBSTITUTION_CHAR, MASK_SOURCE_CHAR, LONG_MOMENT_FORMAT } from '@bp/shared/models/core';

import type { PropertyMetadataCustomValueViewDirective } from './property-metadata-custom-view.directive';

@Component({
	selector: 'bp-property-metadata-view',
	templateUrl: './property-metadata-view.component.html',
	styleUrls: [ './property-metadata-view.component.scss' ],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyMetadataViewComponent<TEntity> implements OnChanges {
	// eslint-disable-next-line @typescript-eslint/naming-convention
	FieldViewType = FieldViewType;

	@Input() label = true;

	@Input() compact = false;

	@Input() metadata!: PropertyMetadata;

	@Input() table?: PropertyMetadataTable | null;

	@Input() value: any;

	@Input() tooltip?: string | null;

	@Input() customValueTemplate?: TemplateRef<any> | null;

	@Input() customValueView?: PropertyMetadataCustomValueViewDirective<TEntity> | null;

	readonly defaultMomentFormat = LONG_MOMENT_FORMAT;

	get showLabel(): boolean {
		return this.customValueView
			? !!this.customValueView.showLabel
			: this.label;
	}

	get viewType(): FieldViewType {
		return this.table?.viewType ?? this.metadata.viewType;
	}

	get viewFormatter(): ((propertyValue: any) => any) | null {
		return this.table?.viewFormatter ?? this.metadata.viewFormatter;
	}

	get viewEmptyValue(): number | string | null {
		return this.table?.viewEmptyValue ?? this.metadata.viewEmptyValue;
	}

	get isCompact(): boolean {
		return !!this.table || !!this.compact;
	}

	// @Input() color: ThemePalette;

	get booleanIcon(): string {
		return this.value ? 'check' : 'close';
	}

	formattedValue: any;

	constructor(
		private readonly _renderer: Renderer2,
		private readonly _host: ElementRef,
		private readonly _displayValuePipe: DisplayValuePipe,
	) { }

	ngOnChanges({ metadata, value, table }: Partial<SimpleChanges>): void {
		metadata && this._setHostClass(metadata);

		if (metadata || value || table)
			// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
			this.formattedValue = this._buildFormattedValue();
	}

	isEnumeration(value: unknown): value is Enumeration {
		return value instanceof Enumeration;
	}

	private _setHostClass({ previousValue: previous, currentValue: current }: SimpleChange): void {
		previous && this._renderer.removeClass(this._host.nativeElement, this._getHostClass(previous));

		current && this._renderer.addClass(this._host.nativeElement, this._getHostClass(current));
	}

	private _getHostClass(md: PropertyMetadata): string {
		return `view-type-${ md.viewType.cssClass }`;
	}

	private _buildFormattedValue(): any {
		if (this.viewFormatter)
			return this.viewFormatter(this.value);

		const displayValue = this._displayValuePipe.transform(this.value);

		return this.metadata.control.isSecret
			? this._replaceMaskSymbolsInString(displayValue)
			: displayValue;
	}

	private _replaceMaskSymbolsInString<T>(value: T): T | string {
		// Note displayValue pipe actually returns `any` value, so need to have this explicit check.
		return typeof value === 'string'
			? value.replaceAll(MASK_SOURCE_CHAR, MASK_SUBSTITUTION_CHAR)
			: value;
	}

}
